import React from 'react';
import styles from './UsageMeter.module.css';
import { classNames, formatNumber } from '../../utils/helpers';

/**
 * Progress bar for "used of limit" figures. Turns amber at 80% and red when full.
 * With `unlimited` it shows the count only, without a bar.
 */
function UsageMeter({
  used = 0,
  limit = 0,
  unlimited = false,
  label,
  caption,
  size = 'md',
  showNumbers = true,
  className,
}) {
  const safeUsed = Number(used) || 0;
  const safeLimit = Number(limit) || 0;

  if (unlimited) {
    return (
      <div className={classNames(styles.meter, styles[size], className)}>
        <div className={styles.meta}>
          {label && <span className={styles.label}>{label}</span>}
          <span className={styles.numbers}>
            <strong>{formatNumber(safeUsed)}</strong>
            <span className={styles.of}> signed · </span>
            <span className={styles.unlimitedTag}>Unlimited</span>
          </span>
        </div>
        {caption && <span className={styles.caption}>{caption}</span>}
      </div>
    );
  }

  const percent = safeLimit > 0 ? Math.min(100, (safeUsed / safeLimit) * 100) : safeUsed > 0 ? 100 : 0;
  const tone = percent >= 100 ? 'full' : percent >= 80 ? 'high' : 'normal';

  return (
    <div className={classNames(styles.meter, styles[size], className)}>
      {(label || showNumbers) && (
        <div className={styles.meta}>
          {label && <span className={styles.label}>{label}</span>}
          {showNumbers && (
            <span className={styles.numbers}>
              <strong>{formatNumber(safeUsed)}</strong>
              <span className={styles.of}> / {formatNumber(safeLimit)}</span>
            </span>
          )}
        </div>
      )}
      <div
        className={styles.track}
        role="progressbar"
        aria-label={label || 'Documents used'}
        aria-valuemin={0}
        aria-valuemax={safeLimit}
        aria-valuenow={Math.min(safeUsed, safeLimit)}
      >
        <div className={classNames(styles.fill, styles[tone])} style={{ width: `${percent}%` }} />
      </div>
      {caption && <span className={styles.caption}>{caption}</span>}
    </div>
  );
}

export default UsageMeter;
